import { defineMessages, IntlShape } from "react-intl";

/**
 * Order metadata written by the storefront on checkout creation (BAM-32).
 * The keys are the storefront's contract; the channel values are its enum.
 * `acquisition.*` is the first touch, `acquisition.last.*` the last non-direct
 * landing before the order.
 */
export const ACQUISITION_PREFIX = "acquisition.";
export const ACQUISITION_LAST_PREFIX = "acquisition.last.";

export function acquisitionMetadataKey(
  name: string,
  prefix: string = ACQUISITION_PREFIX,
): string {
  return `${prefix}${name}`;
}

export const ACQUISITION_CHANNEL_METADATA_KEY =
  acquisitionMetadataKey("channel");

export enum AcquisitionChannel {
  meta = "meta",
  google = "google",
  tiktok = "tiktok",
  tradetracker = "tradetracker",
  newsletter = "newsletter",
  referral = "referral",
  direct = "direct",
}

export const acquisitionChannelMessages = defineMessages({
  meta: {
    id: "Nt6mke",
    defaultMessage: "Meta",
    description: "acquisition channel",
  },
  google: {
    id: "SbPj8B",
    defaultMessage: "Google",
    description: "acquisition channel",
  },
  tiktok: {
    id: "VcV1b4",
    defaultMessage: "TikTok",
    description: "acquisition channel",
  },
  tradetracker: {
    id: "zzdMFI",
    defaultMessage: "TradeTracker",
    description: "acquisition channel",
  },
  newsletter: {
    id: "DSZX1R",
    defaultMessage: "Newsletter",
    description: "acquisition channel",
  },
  referral: {
    id: "1AD/iD",
    defaultMessage: "Referral",
    description: "acquisition channel",
  },
  direct: {
    id: "Q0Q1DD",
    defaultMessage: "Direct",
    description: "acquisition channel",
  },
});

export interface MetadataItem {
  key: string;
  value: string;
}

export function getMetadataValue(
  metadata: MetadataItem[] | null | undefined,
  key: string,
): string | undefined {
  return metadata?.find(item => item.key === key)?.value || undefined;
}

function isAcquisitionChannel(value: string): value is AcquisitionChannel {
  return (Object.values(AcquisitionChannel) as string[]).includes(value);
}

export function getAcquisitionChannelLabel(
  intl: IntlShape,
  channel: string | undefined,
): string | undefined {
  if (!channel) {
    return undefined;
  }

  // An unmapped value means the storefront's enum grew; show it raw rather than hide it.
  return isAcquisitionChannel(channel)
    ? intl.formatMessage(acquisitionChannelMessages[channel])
    : channel;
}

// "Meta (facebook / paid)": channel first, then the utm pair when present.
export function formatAcquisition(
  intl: IntlShape,
  metadata: MetadataItem[] | null | undefined,
  prefix: string = ACQUISITION_PREFIX,
): string | undefined {
  const channel = getAcquisitionChannelLabel(
    intl,
    getMetadataValue(metadata, acquisitionMetadataKey("channel", prefix)),
  );

  if (!channel) {
    return undefined;
  }

  const utm = [
    getMetadataValue(metadata, acquisitionMetadataKey("utm_source", prefix)),
    getMetadataValue(metadata, acquisitionMetadataKey("utm_medium", prefix)),
  ]
    .filter(Boolean)
    .join(" / ");

  return utm ? `${channel} (${utm})` : channel;
}

export function getAcquisitionChannelChoices(intl: IntlShape) {
  return Object.values(AcquisitionChannel).map(channel => ({
    label: getAcquisitionChannelLabel(intl, channel) ?? channel,
    value: channel,
  }));
}
